import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import { Text } from '../../typography';
import Modal from '../Modal';
import styles from './CompleteTaskModal.module.scss';
import { useCurrentUser } from '../../../providers/UserProvider';
import { taskStatus } from '../../../utilities/tasks';
import {
  taskIntervalStringLookup,
  getRoute,
  paths,
} from '../../../constants/strings';
import { useTranslation } from 'react-i18next';
import Icon from '../../Icon';
import { formatTime } from '../../../utilities/time';
import {
  assetTypeIcons,
  customColors,
  vehicleString,
} from '../../../utilities';
import Label from '../../action_items/Label';
import moment from 'moment';
import SimpleWorkspaceCard from '../../SimpleWorkspaceCard';
import { keys } from '../../../utilities/translator/translation_keys';
import TextWithIcon from '../../typography/TextWithIcon';
import UserMultiSelector from '../../UserMultiSelector';
import Dropdown from '../../dropdownV2/Dropdown';
import { Form } from 'react-bootstrap';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import Spacer from '../../typography/Spacer';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInspection } from '../../../hooks/offline-hooks/createInspectionhook';
import { showToast } from '../../../graphql/cache/modal';

const getTaskById = loader('../../../pages/fleet/VehicleSchedule.graphql');

export default function CompleteTaskModal() {
  const { modalState, closeModal, updateModal } = useModal();
  const { allTemplateTypes, user, companyLocations } = useCurrentUser();
  const { t } = useTranslation();
  const { workspaceLocations, workspaceId, allWorkspaces } = useWorkspace();
  const navigate = useNavigate();
  const { addInspection } = useInspection();

  const {
    task: stateTask,
    onStart,
    show,
    participants = [],
    location,
    template,
    asset,
    workspace,
    taskId,
  } = modalState.completeTaskModal;

  const onHide = () => closeModal({ modalName: modals.completeTask });

  const { data: { getTasks: [fetchedTask] = [{}] } = {} } = useQuery(
    getTaskById,
    {
      skip: !show || !!stateTask?.id || !taskId,
      variables: {
        options: {
          filters: [
            { field: 'id', operator: 'eq', value: [taskId?.toString()] },
          ],
        },
      },
    },
  );

  const task = useMemo(() => {
    if (stateTask?.id) {
      return stateTask;
    }
    return fetchedTask ?? {};
  }, [stateTask, fetchedTask]);

  const handleStart = () => {
    onHide();
    if (stateTask?.id) {
      onStart?.(participants);
      return;
    }
    const isPrivate =
      task.template?.templateType?.key === 'INCIDENT' ||
      task.template?.private === true;
    addInspection({
      templateId: task.template?.id,
      vehicleId: vehicle?.id,
      inspectionType: task.template?.templateType,
      participants: participants?.map((u) => u.id) ?? [user.id],
      workspaceId:
        allWorkspaces || task.template?.templateType?.key === 'INCIDENT'
          ? null
          : task.workspace?.id,
      privateAssessment: isPrivate,
      subject: `${task.title}`,
    }).then((res) => {
      const id = res?.data?.addInspection?.id;
      const err = res?.errors;
      if (err?.length) {
        showToast({
          variant: 'warning',
          title: 'Error',
          message: `There was a problem starting the assessment. Error: ${err[0].message}`,
        });
      }
      if (!id) return;
      navigate(
        getRoute(task.workspace?.id || workspaceId, paths.assessment, id),
      );
    });
  };

  const update = (variables) =>
    updateModal({
      modalName: modals.completeTask,
      variables: { ...variables },
    });

  const templateType = template?.templateType?.toLowerCase();
  const templateTypeInfo = allTemplateTypes?.[templateType];
  const templateIcon = templateTypeInfo?.icon || 'description';
  const templateColor =
    customColors[templateTypeInfo?.color?.toUpperCase()] ||
    templateTypeInfo?.color ||
    customColors.SECONDARY;

  const myAssigneeInfo = useMemo(
    () =>
      task.assigneeInfo?.find((a) => a.assignee?.id === user?.id) ??
      task.assigneeInfo?.[0],
    [task, user?.id],
  );
  const vehicle = myAssigneeInfo?.vehicle ?? asset ?? null;

  const nextDueLabel = (() => {
    if (task.interval === 'DAILY') return 'Today';
    if (task.interval === 'WEEKLY') return moment().day(7).format('MMM D');
    if (task.interval === 'MONTHLY')
      return moment().add(1, 'month').startOf('month').format('MMM D');
    return task.scheduledEndDate ? formatTime(task.scheduledEndDate) : null;
  })();

  const locations = !!workspace?.id ? workspaceLocations : companyLocations;

  return (
    <Modal
      className={styles.modal}
      onClose={onHide}
      title={`✅ ${task.title}`}
      submitText="Start"
      onSubmit={handleStart}
      hideDelete
      open={show || false}
      submitDisabled={template?.templateOptions?.location === true && !location}
    >
      <div className={styles.content}>
        {myAssigneeInfo?.status && (
          <div className={styles.row}>
            <div className={styles.rowLabel}>
              <Text noMargin weight="semiBold">
                Status
              </Text>
            </div>
            <Label
              name={t(taskStatus[myAssigneeInfo?.status]?.title)}
              color={taskStatus[myAssigneeInfo?.status]?.color}
              rowSized={false}
            />
          </div>
        )}
        {task.interval && (
          <div className={styles.row}>
            <div className={styles.rowLabel}>
              <Text noMargin weight="semiBold">
                Interval
              </Text>
            </div>
            <div className={styles.flexRow}>
              <Icon style={{ fontSize: '1.1rem', opacity: 0.5 }}>repeat</Icon>
              <Text noMargin>
                {t(taskIntervalStringLookup[task.interval]) || task.interval}
              </Text>
            </div>
          </div>
        )}

        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <Text noMargin weight="semiBold">
              {t(keys.common.WORKSPACE)}
            </Text>
          </div>
          <div>
            <SimpleWorkspaceCard workspace={workspace} weight="regular" />
          </div>
        </div>

        {task.taskType !== 'OBSERVATION' && template?.title && (
          <div className={styles.row}>
            <div className={styles.rowLabel}>
              <Text noMargin weight="semiBold">
                Template
              </Text>
            </div>
            <TextWithIcon
              truncate
              className={styles.maxWidth}
              iconProps={{ color: templateColor, fontSize: '1.3rem' }}
              leftIcon={templateIcon || 'article'}
              noMargin
            >
              {template.title}
            </TextWithIcon>
          </div>
        )}

        {myAssigneeInfo?.lastCompleted && (
          <div className={styles.row}>
            <div className={styles.rowLabel}>
              <Text noMargin size="md" color="secondary">
                Last Completed
              </Text>
            </div>
            <TextWithIcon leftIcon="event" noMargin size="md" weight="semiBold">
              {formatTime(myAssigneeInfo.lastCompleted)}
            </TextWithIcon>
          </div>
        )}

        {nextDueLabel && (
          <div className={styles.row}>
            <div className={styles.rowLabel}>
              <Text noMargin weight="semiBold">
                {t(keys.action_items.DUE_DATE)}
              </Text>
            </div>
            <TextWithIcon
              leftIcon="event"
              iconProps={{ fontSize: '1.2rem', color: customColors.SECONDARY }}
              noMargin
            >
              {nextDueLabel}
            </TextWithIcon>
          </div>
        )}

        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <Text noMargin weight="semiBold">
              {t(keys.common.ASSET)}
            </Text>
          </div>
          <div className={styles.flexRow}>
            {vehicle ? (
              <>
                {vehicle.imageUrl ? (
                  <img
                    className={styles.avatar}
                    src={vehicle.imageUrl}
                    alt="vehicle"
                  />
                ) : (
                  <Icon style={{ fontSize: '1.2rem' }}>
                    {assetTypeIcons?.[vehicle?.type] || 'local_shipping'}
                  </Icon>
                )}
                <Text noMargin size="md">
                  {vehicleString(vehicle)}
                </Text>
              </>
            ) : (
              <Text noMargin size="md" color="secondary">
                None
              </Text>
            )}
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {template?.templateOptions?.location === true ? (
          <>
            <div className={styles.spacer} />
            <Text
              weight="semiBold"
              noMargin
              size="sm-md"
              className={styles.title}
            >
              {t(keys.common.LOCATION)}
              <span className={styles.red}>*</span>
            </Text>
            {!!locations?.length ? (
              <Dropdown
                required
                selected={
                  location ? { label: location, value: location } : null
                }
                options={locations}
                onChange={(obj) => update({ location: obj?.value ?? null })}
              />
            ) : (
              <div>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={location}
                  onChange={(e) => update({ location: e.target.value })}
                />
                <div className={styles.spacer} />
              </div>
            )}
            <Spacer />
          </>
        ) : null}
        <div className={styles.participants}>
          <Text
            weight="semiBold"
            noMargin
            className={styles.title}
            size="sm-md"
          >
            {t(keys.common.PARTICIPANTS)}
          </Text>
          <UserMultiSelector
            stateUpdateOnly
            setUsers={(p) => update({ participants: p })}
            selected={participants}
            type={task.workspace?.id ? 'workspace' : 'all'}
            otherWorkspace={task.workspace}
            onUserAdded={(u) => {
              update({ participants: [...participants, u] });
            }}
            onUserRemoved={(u) => {
              update({
                participants: participants.filter((p) => p.id !== u.id),
              });
            }}
          />
        </div>
      </div>
      <br />
      <br />
    </Modal>
  );
}
